import React, { useMemo, memo } from "react";
import type { BiddingInfoResult, BiddingInfoData } from "../../../types";
import { useTruncatedTooltip } from "../../common/TruncatedTooltip";
import {
  CaltivationCategoryOptions,
  ProductionHistoryOptions,
} from "../../../constants";
import dayjs from "dayjs";
import { useAuth } from "../../../contexts/AuthContext";
import { UserRole } from "../../../types/UserRole";
import { ApprovalStatusConst } from "../../../constants";
import Big from "big.js";
import { useAppDialog } from "../../../hooks/useAppDialog";

// DBのUTC文字列を受け取り、日本時間のミリ秒付き文字列に変換する
const formatJSTWithMillis = (dateStr: string | undefined | null) => {
  if (!dateStr) return "-";
  // .format() を通すことで、余計なミリ秒をカットし、区切り文字を整えます。
  return dayjs(dateStr).format("YYYY/MM/DD HH:mm:ss.SSS");
};

//----------------
// Props
//----------------
interface BidConfirmationItemRowProps {
  item: BiddingInfoResult;
  isExpanded: boolean;
  onToggle: () => void;
  isChecked: boolean;
  onCheck: () => void;
  onToggleBidDisabled: (
    itemId: string,
    branchNumber: number,
    buyerCode: number,
    isDisabled: boolean,
  ) => void;
  onStatusChange: (
    itemId: string,
    branchNumber: number,
    nextStatus: number,
  ) => void;
  setIsOverlayActive: (active: boolean) => void;
}

//--------------------------------
// 落札結果の行コンポーネント
//--------------------------------
export const BidConfirmationItemRow = memo(
  ({
    item,
    isExpanded,
    onToggle,
    isChecked,
    onCheck,
    onToggleBidDisabled,
    onStatusChange,
  }: BidConfirmationItemRowProps) => {
    const { tooltipProps, TooltipElement } = useTruncatedTooltip(
      "font-medium truncate pointer-events-auto",
    );

    const { currentUser } = useAuth(); // 認証Context
    const { showConfirm, OverlayElement } = useAppDialog();

    //----------------
    // Memo
    //----------------
    // 総量計算
    const totalWeight = useMemo(() => {
      try {
        // 1. 各値を Big オブジェクトに変換
        const c1 = new Big(item.capacity1 || 0);
        const q1 = new Big(item.quantity1 || 0);
        const c2 = new Big(item.capacity2 || 0);
        const q2 = new Big(item.quantity2 || 0);

        // 2. 10進数で正確に計算
        // w1 = c1 * q1
        const w1 = c1.times(q1);
        // w2 = c2 * q2
        const w2 = c2.times(q2);

        // 3. 結果を合算して number 型で返す
        return w1.plus(w2).toNumber();
      } catch (e) {
        // 数値として読み込めない場合などのセーフティ
        return 0;
      }
    }, [item.capacity1, item.quantity1, item.capacity2, item.quantity2]);

    // 最高落札価格の表示
    const displayHighestPrice = useMemo(() => {
      if (!item.highestBidPrice || item.highestBidPrice === 0)
        return (
          <span className="text-slate-400">
            <br />
          </span>
        );
      return (
        <span className="text-red-600 font-bold">
          {Number(item.highestBidPrice).toLocaleString()}
        </span>
      );
    }, [item.highestBidPrice]);

    // デバッグ用ハンドラ
    const handleClick = (e: React.MouseEvent) => {
      e.stopPropagation(); // 念の為バブリング防止
      console.log("Row clicked:", item.itemId); // ★コンソールにIDが出るか確認
      onToggle();
    };

    const handleToggleCheck = (e: React.MouseEvent) => {
      e.stopPropagation(); // 行の開閉を止める
      onCheck(); // 親（Page側）のリストを更新してもらう
    };

    //----------------
    // Render
    //----------------
    return (
      <div
        className={`mt-2 lg:mt-4 border-b lg:pb-0 border-slate-800 lg:border lg:rounded-lg lg:shadow-sm overflow-hidden bg-white ${isExpanded ? "ring-2 ring-indigo-500 rounded-lg" : ""}`}
      >
        {/* フックが提供する背景レイヤー */}
        {OverlayElement}

        {/* === ヘッダー行 (常に表示) === */}
        <div className="p-2 lg:p-4 cursor-pointer" onClick={handleClick}>
          <div
            className={`grid grid-cols-6 md:grid-cols-15 gap-2 items-center text-sm pointer-events-none`}
          >
            <div className="col-span-1 order-1 lg:order-none lg:col-span-1 min-w-[3rem] flex flex-col items-center">
              <div
                onClick={handleToggleCheck}
                className="pointer-events-auto p-1 -ml-8 rounded-md hover:bg-slate-100 transition-colors cursor-pointer flex items-center justify-center"
              >
                <input
                  type="checkbox"
                  checked={isChecked} // 親から渡された状態を反映
                  onChange={(e) => e.stopPropagation()}
                  className="w-5 h-5 cursor-pointer accent-indigo-600 rounded border-slate-300 transition-all"
                />
              </div>
            </div>

            {/* 1. 入札番号 */}
            <div className="col-span-1 lg:col-span-1 order-1 lg:order-none">
              <p className="hidden md:block text-xs text-slate-500 mb-0.5">
                入札No.
              </p>
              <p className="text-base lg:font-bold text-slate-800">
                {item.biddingNumber || "-"}
              </p>
            </div>
            {/* 2. 売り手 */}
            <div className="col-span-4 lg:col-span-2 order-2 lg:order-none">
              <p className="hidden md:block text-xs text-slate-500 mb-0.5">
                売手
              </p>
              <p
                {...tooltipProps}
                className="text-lg lg:font-bold pointer-events-auto truncate"
              >
                {item.sellerName}
              </p>
            </div>
            {/* 3. 生産者 */}
            <div className="col-span-4 lg:col-span-2 order-3 lg:order-none">
              <p className="hidden lg:block text-xs text-slate-500 mb-0.5">
                生産者
              </p>
              <p
                {...tooltipProps}
                className="text-lg lg:font-bold pointer-events-auto truncate"
              >
                {item.farmerName}
              </p>
            </div>
            {/* 7. 荷印 */}
            <div className="col-span-2 lg:col-span-1 order-4 lg:order-none">
              <p className="hidden lg:block text-xs text-slate-500 mb-0.5">
                荷印
              </p>
              <p
                {...tooltipProps}
                className="text-lg lg:font-bold pointer-events-auto truncate"
              >
                {item.brand}
              </p>
            </div>
            {/* PCでは非表示・茶期・茶種・品種 */}
            {/* 4. 茶期 */}
            <div className="col-span-1 hidden md:block">
              <p className="text-xs text-slate-500 mb-0.5">茶期</p>
              <p {...tooltipProps} className="text-lg font-bold truncate">
                {item.teaSeasonName}
              </p>
            </div>
            {/* 5. 茶種 */}
            <div className="col-span-1 hidden md:block">
              <p className="text-xs text-slate-500 mb-0.5">茶種</p>
              <p {...tooltipProps} className="text-lg font-bold truncate">
                {item.teaTypeName}
              </p>
            </div>
            {/* 6. 品種 */}
            <div className="col-span-1 hidden md:block">
              <p className="text-xs text-slate-500 mb-0.5">品種名</p>
              <p {...tooltipProps} className="text-lg font-bold pointer-events-auto truncate">
                {item.varietyName}
              </p>
            </div>
            {/* 8. 総量 */}
            <div className="hidden lg:block col-span-1 text-right">
              <p className="hidden md:block text-xs text-slate-500 mb-0.5">
                総量(kg)
              </p>
              <p className="text-lg lg:font-bold">
                {totalWeight.toLocaleString()}
              </p>
            </div>
            {/* 9. 自身の入札価格 */}
            {currentUser?.role === UserRole.Buyer && (
              <div className="hidden lg:block col-span-1 text-right border-l border-slate-100 pl-2">
                <p className="text-xs text-slate-500 mb-0.5">入札額(円)</p>
                <p className="text-lg font-bold text-blue-700">
                  {Number(item.myBidPrice).toLocaleString()}
                </p>
              </div>
            )}

            {/* 10. 最高落札価格 */}
            <div className="hidden lg:block col-span-2 text-right border-l border-slate-100 pl-2">
              <p className="text-[11px] text-slate-500 mb-0.5">
                最高落札値(円)
              </p>
              <p className="text-lg">{displayHighestPrice}</p>
            </div>
            {/* 行ごとの承認状態  */}
            <div className="hidden lg:block col-span-2 text-right border-l border-slate-100 pl-2 mt-1">
              <p className="text-[11px] text-slate-500 mb-0.5 !text-left">
                承認状態
              </p>
              <select
                className="w-full p-1 border border-slate-300 rounded text-sm bg-white pointer-events-auto focus:ring-1 focus:ring-indigo-500 outline-none"
                value={item.approvalStatus}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => {
                  const nextStatus = Number(e.target.value);
                  onStatusChange(item.itemId, item.branchNumber, nextStatus);
                }}
              >
                <option value={ApprovalStatusConst.UNAPPROVED} className="">
                  未承認
                </option>
                <option value={ApprovalStatusConst.APPROVED} className="">
                  承認済（成立）
                </option>
                <option value={ApprovalStatusConst.DISAPPROVAL} className="">
                  承認済（不成立）
                </option>
              </select>
            </div>
          </div>
        </div>

        <div className="lg:hidden w-full mt-1">
          {/* flex + divide-x で、要素の数に合わせて自動等分 & 境界線 */}
          <div className="flex items-center w-full bg-slate-100 rounded-lg py-1 -translate-y-3 divide-x divide-slate-300">
            {/* 最高落札価格 (共通) */}
            <div className="flex-1 px-1 text-center">
              <p className="text-lg font-semibold">{displayHighestPrice}</p>
            </div>
          </div>
        </div>

        {/* モバイル用: 隠れている項目の簡易表示 */}
        <div className="md:hidden text-base flex flex-wrap gap-x-3 pointer-events-none justify-left ml-2.5 -translate-y-1.5">
          <span>{item.teaSeasonName}</span>
          <span>/</span>
          <span>{item.teaTypeName}</span>
          <span>/</span>
          <span>{item.varietyName}</span>
          <span>/</span>
          <span>{totalWeight.toLocaleString()}kg</span>
        </div>

        {/* === アコーディオン詳細 === */}
        {isExpanded && (
          <div className="p-1 lg:p-4 bg-slate-50 border-t border-slate-200 animate-fadeIn">
            {/* 詳細属性 (GAP区分, 栽培区分, 生産履歴, 備考) */}
            <div className="grid grid-cols-3 lg:grid-cols-4 md:grid-cols-4 lg:gap-6 lg:mb-6 lg:pb-4">
              {/* 1. GAP区分 */}
              <div>
                <p className="hidden lg:block text-xs font-bold text-slate-500 uppercase mb-1">
                  GAP区分
                </p>
                <p className="text-base text-slate-700 lg:font-semibold truncate">
                  {item.gapName}
                </p>
              </div>

              {/* 2. 栽培区分 */}
              <div>
                <p className="hidden lg:block text-xs font-bold text-slate-500 uppercase mb-1">
                  栽培区分
                </p>
                {(() => {
                  const targetOption = CaltivationCategoryOptions.find(
                    (opt) => opt.value === item.caltivationCategory,
                  );
                  return (
                    <p className="text-base text-slate-700 lg:font-semibold">
                      {targetOption ? targetOption.label : "-"}
                    </p>
                  );
                })()}
              </div>

              {/* 3. 生産履歴 */}
              <div>
                <p className="hidden lg:block text-xs font-bold text-slate-500 uppercase mb-1">
                  生産履歴
                </p>
                {(() => {
                  const targetOption = ProductionHistoryOptions.find(
                    (opt) => opt.value === item.productionHistory,
                  );
                  return (
                    <p className="text-base text-slate-700 lg:font-semibold">
                      {targetOption ? targetOption.label : "-"}
                    </p>
                  );
                })()}
              </div>

              {/* 4. 備考 (Buyer以外の場合のみ表示) */}
              <div className="col-span-full lg:col-span-1 mt-1 lg:border-none lg:pt-0 lg:mt-0">
                <p className="hidden lg:block text-xs font-bold text-slate-500 uppercase">
                  備考
                </p>
                <p
                  {...tooltipProps}
                  className="text-base text-slate-700 lg:font-semibold truncate"
                >
                  {item.biko || "-"}
                </p>
              </div>
            </div>

            {/* 容量詳細 & 最低落札値 */}
            <div className="flex items-end w-full">
              {/* 1. 容量① */}
              <div className="flex-1 min-w-0 pb-1">
                <p className="hidden lg:block text-xs font-bold text-slate-500 mb-1">
                  容量①
                </p>
                <p className="text-base text-slate-700 lg:font-semibold truncate">
                  {item.capacity1}kg × {item.quantity1}本
                </p>
              </div>
              {/* 2. 容量② */}
              {!!item.capacity2 && (
                <div className="flex-1 min-w-0 pl-2 pb-1">
                  <p className="hidden lg:block text-xs font-bold text-slate-500 mb-1">
                    容量②
                  </p>
                  <p className="text-base text-slate-700 lg:font-semibold -ml-1 lg:ml-0 truncate ">
                    {item.capacity2}kg × {item.quantity2}本
                  </p>
                </div>
              )}
              {/* 3. 最低落札値 */}
              <div className="flex-1 min-w-0 pl-2 pb-1">
                <p className="hidden lg:block lg:text-xs font-bold text-slate-500 uppercase mb-1 leading-none">
                  最低落札値
                </p>
                <p className="text-base lg:font-bold text-slate-700 -ml-1 lg:ml-0 truncate">
                  {Number(item.bidMinPrice).toLocaleString()}
                </p>
              </div>
            </div>

            {/* 入札履歴テーブル (Adminのみ) */}
            {currentUser?.role === UserRole.Admin && (
              <div className="border-t border-slate-200 mt-1 ">
                <h4 className="text-sm font-bold text-slate-700 mb-3 mt-3 flex items-center gap-2">
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    ></path>
                  </svg>
                  入札履歴
                </h4>
                {!item.biddingInfoDatas ||
                item.biddingInfoDatas.length === 0 ? (
                  <p className="text-sm text-slate-400 italic p-2">
                    入札履歴はありません。
                  </p>
                ) : (
                  <div className="overflow-x-auto border rounded-md bg-white">
                    <table className="w-full text-sm text-left text-slate-600">
                      <thead className="text-xs text-slate-500 uppercase bg-slate-100 border-b">
                        <tr>
                          <th className="px-4 py-2">入札日時</th>
                          <th className="px-4 py-2">買手名称</th>
                          <th className="px-4 py-2 text-right">入札本数①</th>
                          <th className="px-4 py-2 text-right">入札本数②</th>
                          <th className="px-4 py-2 text-right">入札総量</th>
                          <th className="px-4 py-2 text-right">入札値</th>
                          <th className="px-4 py-2 text-center">結果</th>
                          <th className="px-4 py-2 text-center">無効</th>
                        </tr>
                      </thead>
                      <tbody>
                        {item.biddingInfoDatas.map((bid, index) => (
                          <HistoryRow
                            key={`${bid.buyerCode}-${index}`}
                            bid={bid}
                            parentItem={item}
                            onToggleDisabled={onToggleBidDisabled}                            
                            showConfirm={showConfirm}
                          />
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        {TooltipElement}
      </div>
    );
  },
);

const HistoryRow = ({
  bid,
  parentItem,
  onToggleDisabled,
  showConfirm,
}: {
  bid: BiddingInfoData;
  parentItem: BiddingInfoResult;
  onToggleDisabled: (
    itemId: string,
    branchNumber: number,
    buyerCode: number,
    isDisabled: boolean,
  ) => void;
  showConfirm: any; // 共通フックの型
}) => {
  const bidTotalWeight = useMemo(() => {
    const w1 = (bid.quantity1 || 0) * (Number(parentItem.capacity1) || 0);
    const w2 = (bid.quantity2 || 0) * (Number(parentItem.capacity2) || 0);
    return w1 + w2;
  }, [bid, parentItem]);

  // 修正：DBのフラグから判定するようにした。
  const isWinner = bid.isWinningBid;

  const formattedDate = formatJSTWithMillis(bid.bidTimestamp);

  return (
    <tr
      className={`border-b last:border-0 hover:bg-slate-50 ${isWinner ? "bg-yellow-50" : ""}`}
    >
      <td className="px-4 py-2 whitespace-nowrap text-xs text-slate-500">
        {formattedDate}
      </td>
      <td className="px-4 py-2 font-medium">{bid.buyerName}</td>
      <td className="px-4 py-2 text-right">{bid.quantity1}</td>
      <td className="px-4 py-2 text-right">{bid.quantity2}</td>
      <td className="px-4 py-2 text-right font-medium">{bidTotalWeight} kg</td>
      <td className="px-4 py-2 text-right font-bold text-slate-800">
        {bid.bidPrice.toLocaleString()} 円
      </td>
      <td className="px-4 py-2 text-center">
        {isWinner && (
        <span className="inline-block px-2 py-0.5 rounded text-xs font-bold bg-red-100 text-red-600 border border-red-200">
            {/* バックエンドの判定（(抽)）を出す */}
            {parentItem.resultMsg}
          </span>
        )}
      </td>
      <td className="px-4 py-2 text-center">
        <input
          type="checkbox"
          checked={bid.isDisabled || false}
          onChange={(e) => {
            e.stopPropagation();
            const nextState = e.target.checked;
            const actionText = nextState ? "無効に" : "有効に";
            const message = `${bid.buyerName} 様の入札（${bid.bidPrice.toLocaleString()}円）を${actionText}しますか？`;

            // --- 共通フックの showConfirm 呼び出し ---
            showConfirm({
              title: "落札確定",
              message: message,
              onConfirm: () => {
                onToggleDisabled(
                  parentItem.itemId,
                  parentItem.branchNumber,
                  bid.buyerCode,
                  nextState,
                );
              }
            });
          }}
          className="w-5 h-5 cursor-pointer accent-indigo-600 rounded border-slate-300 transition-all"
        />
      </td>
    </tr>
  );
};